/* eslint-disable indent */
// 1、入院记录：入院日期、主诉、入院诊断、现病史（展示几行）
// 2、出院记录：出院日期、出院诊断、入院情况（展示几行）
// 3、影像报告：报告图片/视频、检查日期、检查部位、影像类型、检查结论、dicom/dicom预览链接（有才展示，无则不展示）
// 4、手术信息：手术日期、手术名称、手术经过（展示几行）
// 5、病理报告：检查日期、病理诊断、免疫组化
// 6、检验报告：检验日期、检验项目、检验结果（显示异常指标值，高或低于参考范围的值）
// 7、基因检测：检测日期、检测项目、样本类型、结果描述
// 8、用药记录：医嘱日期、用药方案、不良反应、治疗效果

import usePermission from '@/hooks/usePermission';
import { httpPatientInfoDetail, httpPatientInfoDetailWithArchive } from '@/services/patient';
import { parseInspectionReport } from '@/utils/formatMedicalRecords';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { PatientInfoAtom, PatientInfoStatus } from '../detail';

/**
 * 记录类型对应的数据字段
 * @param type 记录类型
 * @returns 记录类型对应的数据字段
 */
export const useFormField = (type: string) => {
  const formField: any = {
    medicalRecord: [
      // { label: '入院日期', value: 'recordTime' },
      { label: '主诉', value: 'chiefComplaint' },
      { label: '入院诊断', value: 'diagnosis' },
      { label: '现病史', value: 'presentIllness' },
    ],
    hospitalDischargeRecords: [
      // { label: '出院日期', value: 'dischargeTime' },
      { label: '出院诊断', value: 'dischargeDiagnosis' },
      { label: '入院情况', value: 'admitDescription' },
    ],
    imageReport: [
      // { label: '检查日期', value: 'recordTime' },
      { label: '检查部位', value: 'parts' },
      { label: '影像类型', value: 'imagesType', dictKey: 'imageType' },
      { label: '检查结论', value: 'reportDiagnose' },
      { label: 'dicom', value: 'storeUid' },
      { label: 'dicom预览链接', value: 'dicomQrcodeUrl' },
    ],
    surgicalRecord: [
      // { label: '手术日期', value: 'recordTime' },
      { label: '手术名称', value: 'surgeryName' },
      { label: '手术经过', value: 'procedures' },
    ],
    pathologyReport: [
      // { label: '检查日期', value: 'recordTime' },
      { label: '病理诊断', value: 'summary' },
      { label: '免疫组化', value: 'immunohistochemistry' },
    ],
    inspectionReport: [
      // { label: '检查日期', value: 'recordTime' },
      { label: '检验项目', value: 'type' },
      { label: '检验结果', value: 'inspectionItems' },
    ],
    geneticTesting: [
      // { label: '检查日期', value: 'recordTime' },
      { label: '检测项目', value: 'itemName' },
      // { label: '样本类型', value: 'sampleType' },
      { label: '结果描述', value: 'description' },
    ],
    drugRecord: [
      // { label: '医嘱日期', value: 'recordTime' },
      { label: '用药方案', value: 'drugPlan' },
      { label: '不良反应', value: 'adverseReactions' },
      // { label: '治疗效果', value: 'treatmentEffect' },
    ],
    admission24hDischargeRecord: [
      // 出院日期、出院诊断、主诉、入院情况（展示几行）
      { label: '出院诊断', value: 'dischargeDescription' },
      { label: '主诉', value: 'chiefComplaint' },
      { label: '入院情况', value: 'admissionDescription' },
    ],
    outpatientRecord: [
      // 就诊日期、主诉、诊断、现病史（展示几行）
      { label: '主诉', value: 'chiefComplaint' },
      { label: '诊断', value: 'diagnosis' },
      { label: '现病史', value: 'presentIllness' },
    ],
    admissionProgressRecord: [
      // ①首次病程记录：记录日期、病例特点（展示几行）。
      // ②日常病程记录：记录日期、记录内容。
      // （备注：首次病程记录和日常病程记录，有则显示，无则不显示；有多个日常病程记录时，展示第一个日期即可）
      { label: '记录日期', value: 'firstRecordTime' },
      { label: '病例特点', value: 'clinicalFeatures' },
      { label: '日常病程记录', value: 'items' },
    ],
    radiotherapyRecord: [
      // 放疗日期、放疗方案、治疗效果
      { label: '放疗方案', value: 'treatmentPlan' },
      { label: '治疗效果', value: 'therapeuticEffect' },
    ],
    otherInformation: [
      { label: '报告类型', value: 'informationType' },
      { label: '描述', value: 'description' },
    ],
  };

  return formField[type] || formField.medicalRecord;
};

/**
 * 记录类型对应的颜色和图标
 */
export const recordTypeConfig: any = {
  medicalRecord: {
    color: '#0365BF',
    bgColor: 'rgba(3, 101, 191, 0.1)',
    label: '入院记录',
    icon: 'iconfont icon-admission',
  },
  hospitalDischargeRecords: {
    color: '#3ACA77',
    bgColor: 'rgba(58, 202, 119, 0.1)',
    label: '出院记录',
    icon: 'iconfont icon-discharge',
  },
  admission24hDischargeRecord: {
    color: '#34ABB8',
    bgColor: 'rgba(105, 222, 241, 0.1)',
    label: '24h出入院记录',
    icon: 'iconfont icon-a-24time',
  },
  admissionProgressRecord: {
    color: '#FF7A7A',
    bgColor: 'rgba(213, 138, 139, 0.1)',
    label: '住院病程记录',
    icon: 'iconfont icon-hospitalization',
  },
  outpatientRecord: {
    color: '#D9808B',
    bgColor: 'rgba(217, 128, 139, 0.1)',
    label: '门诊记录',
    icon: 'iconfont icon-menzhenjilu',
  },
  imageReport: {
    color: '#FF9D2C',
    bgColor: 'rgba(255, 157, 44, 0.1)',
    label: '影像报告',
    icon: 'iconfont icon-scan',
  },
  pathologyReport: {
    color: '#556D88',
    bgColor: 'rgba(85, 109, 136, 0.1)',
    label: '病理报告',
    icon: 'iconfont icon-pathology',
  },
  surgicalRecord: {
    color: '#187296',
    bgColor: 'rgba(24, 114, 150, 0.1)',
    label: '手术信息',
    icon: 'iconfont icon-surgery',
  },
  geneticTesting: {
    color: '#0494CE',
    bgColor: 'rgba(4, 148, 206, 0.1)',
    label: '基因检测',
    icon: 'iconfont icon-gene',
  },
  inspectionReport: {
    color: '#FFA07A',
    bgColor: 'rgba(255, 160, 122, 0.1)',
    label: '检验报告',
    icon: 'iconfont icon-test',
  },
  drugRecord: {
    color: '#D9808B',
    bgColor: 'rgba(217, 128, 139, 0.1)',
    label: '用药记录',
    icon: 'iconfont icon-medication',
  },
  radiotherapyRecord: {
    color: '#5367CA',
    bgColor: 'rgba(96, 111, 211, 0.1)',
    label: '放疗记录',
    icon: 'iconfont icon-fangliao',
  },
  otherInformation: {
    color: '#FFA07A',
    bgColor: 'rgba(255, 160, 122, 0.1)',
    label: '其他',
    icon: 'iconfont icon-qita',
  },
};

/**
 * 获取记录类型对应的颜色和图标
 * @param type 记录类型
 * @returns 记录类型对应的颜色和图标
 */
export const getRecordTypeConfig = (type: string) => {
  return recordTypeConfig[type] || recordTypeConfig.medicalRecord;
};

export const usePatientDetail = (props?: any) => {
  const { disabled, needDesensi = false } = props;
  const [patientInfo, setPatientInfo] = useRecoilState<any>(PatientInfoAtom);
  const [patientInfoStatus, setPatientInfoStatus] = useRecoilState<any>(PatientInfoStatus);
  const [originFormattedData, setOriginFormattedData] = useState<any[]>([]);
  const [originAllFormattedData, setOriginAllFormattedData] = useState<any[]>([]);
  const [filteredFormattedData, setFilteredFormattedData] = useState<any[]>([]);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
  const permission = usePermission('patientDetail');
  const [params] = useSearchParams();
  const formType = params.get('type');
  // 禁用，不可编辑
  const queryDisabled = params.get('disabled') === '1';
  const patientId = props?.patientId || params.get('id') || '';
  const isShareType = formType === 'share'; // 病例库进来

  useEffect(() => {
    setPatientInfoStatus({
      edit: permission.edit && !disabled && !queryDisabled,
    });
  }, [permission]);

  const fetchPatientInfo = () => {
    setLoadingDetail(true);
    httpPatientInfoDetail(patientId, isShareType || needDesensi)
      .then((res) => {
        setPatientInfo({
          ...res,
        });
        // setPatientDetail(res);
        // const formattedData = formatMedicalRecords(res);
        // console.log(formattedData);
        // setOriginFormattedData(formattedData);
        const records = props?.old
          ? (res?.records || [])?.map((item: any) => {
              return {
                ...item,
                records: item.records?.map((record: any) => {
                  if (record.recordType === 'INSPECTION_REPORT') {
                    return parseInspectionReport(record);
                  }
                  return record;
                }),
              };
            })
          : (res?.records || [])?.map((item: any) => {
              if (item.recordType === 'INSPECTION_REPORT') {
                return {
                  ...parseInspectionReport(item),
                  imagePaths:
                    item.imagePath || item.reportImage
                      ? (item.imagePath || item.reportImage)?.split(',')
                      : [],
                };
              }
              return {
                ...item,
                imagePaths:
                  item.imagePath || item.reportImage
                    ? (item.imagePath || item.reportImage)?.split(',')
                    : [],
              };
            });
        console.log({ records });
        setOriginFormattedData(records);
        setOriginAllFormattedData(records);
        setFilteredFormattedData(records);
      })
      .finally(() => {
        setLoadingDetail(false);
      });
  };

  useEffect(() => {
    patientId && fetchPatientInfo();

    return () => {
      props?.old && setPatientInfo(null);
    };
  }, [patientId]);

  return {
    isShareType,
    patientInfo,
    patientInfoStatus,
    fetchPatientInfo,
    originFormattedData,
    originAllFormattedData,
    filteredFormattedData,
    setOriginFormattedData,
    setOriginAllFormattedData,
    setFilteredFormattedData,
    loadingDetail,
    setLoadingDetail,
  };
};

export const usePatientDetailWithArchive = (props?: any) => {
  const { disabled, needDesensi = false } = props;
  const [patientInfo, setPatientInfo] = useRecoilState<any>(PatientInfoAtom);
  const [patientInfoStatus, setPatientInfoStatus] = useRecoilState<any>(PatientInfoStatus);
  const [originFormattedData, setOriginFormattedData] = useState<any[]>([]);
  const [originAllFormattedData, setOriginAllFormattedData] = useState<any[]>([]);
  const [filteredFormattedData, setFilteredFormattedData] = useState<any[]>([]);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
  const permission = usePermission('patientDetail');
  const [params] = useSearchParams();
  const formType = params.get('type');
  // 禁用，不可编辑
  const queryDisabled = params.get('disabled') === '1';
  const patientId = props?.patientId || params.get('id') || '';
  const isShareType = formType === 'share'; // 病例库进来
  // 轮询定时器引用
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 请求序号引用，用于处理竞态条件，只处理最新一次请求的结果
  const requestIdRef = useRef<number>(0);

  useEffect(() => {
    setPatientInfoStatus({
      edit: permission.edit && !disabled && !queryDisabled,
    });
  }, [permission]);

  /** 清除轮询定时器 */
  const clearPollingTimer = () => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  };

  /**
   * 判断 records 中是否存在 archiving = true 的数据
   * 支持 old 模式下的嵌套结构
   */
  const hasArchivingRecord = (records: any[]): boolean => {
    if (!Array.isArray(records)) return false;
    return records.some((item: any) => {
      // old 模式：records 是嵌套数组
      if (Array.isArray(item.records)) {
        return item.archiving === true || item.records.some((r: any) => r.archiving === true);
      }
      return item.archiving === true;
    });
  };

  const fetchPatientInfo = () => {
    // 生成新的请求序号
    // eslint-disable-next-line no-plusplus
    const currentRequestId = ++requestIdRef.current;
    setLoadingDetail(true);
    httpPatientInfoDetailWithArchive(patientId, isShareType || needDesensi)
      .then((res) => {
        // 如果当前请求不是最新的请求，丢弃结果，避免竞态条件导致的数据混乱
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setPatientInfo({
          ...res,
        });
        const records = (res?.records || [])?.map((item: any) => {
          if (item.recordType === 'INSPECTION_REPORT') {
            return {
              ...parseInspectionReport(item),
              imagePaths:
                item.imagePath || item.reportImage
                  ? (item.imagePath || item.reportImage)?.split(',')
                  : [],
            };
          }
          return {
            ...item,
            imagePaths:
              item.imagePath || item.reportImage
                ? (item.imagePath || item.reportImage)?.split(',')
                : [],
          };
        });
        console.log({ records });
        setOriginFormattedData(records);
        setOriginAllFormattedData(records);
        setFilteredFormattedData(records);

        // 存在归档中的数据时，开启 3 秒轮询
        clearPollingTimer();
        if (hasArchivingRecord(records)) {
          pollingTimerRef.current = setTimeout(() => {
            fetchPatientInfo();
          }, 3000);
        }
      })
      .finally(() => {
        // 只有最新请求才关闭 loading，避免旧请求覆盖状态
        if (currentRequestId === requestIdRef.current) {
          setLoadingDetail(false);
        }
      });
  };

  useEffect(() => {
    patientId && fetchPatientInfo();

    return () => {
      // 组件卸载时清除轮询，避免内存泄漏
      clearPollingTimer();
      props?.old && setPatientInfo(null);
    };
  }, [patientId]);

  return {
    isShareType,
    patientInfo,
    patientInfoStatus,
    fetchPatientInfo,
    originFormattedData,
    originAllFormattedData,
    filteredFormattedData,
    setOriginFormattedData,
    setOriginAllFormattedData,
    setFilteredFormattedData,
    loadingDetail,
    setLoadingDetail,
  };
};
